'use client';

import { useMemo, useRef, useCallback } from 'react';
import { useGardenPlannerStore } from '@shared/store/useGardenPlannerStore';
import { useUpdateGarden, useToggleCellAvailability } from '@api/gardens';
import { createCellSlotsMap, createGetSlotsForCell } from '@modules/garden-planner/business_logic';
import { useGardenPlannerContext } from '@modules/garden-planner/context/garden_planner_context';
import {
  createHandleCellClick,
  isCellAvailable,
} from '@modules/garden-planner/garden_grid/business_logic';
import type { GardenPlannerViews } from '@modules/garden-planner/garden_planner_views';

interface GardenGridContainerProps {
  views: GardenPlannerViews;
}

export function GardenGridContainer({ views }: GardenGridContainerProps) {
  const { garden, plants } = useGardenPlannerContext();
  const { GardenGridView } = views;
  const selectedPlant = useGardenPlannerStore((state) => state.selectedPlant);
  const isAvailabilityMode = useGardenPlannerStore((state) => state.isAvailabilityMode);
  const isCellSettingsMode = useGardenPlannerStore((state) => state.isCellSettingsMode);
  const isClearMode = useGardenPlannerStore((state) => state.isClearMode);
  const setSelectedCell = useGardenPlannerStore((state) => state.setSelectedCell);
  const isBulkEditMode = useGardenPlannerStore((state) => state.isBulkEditMode);
  const bulkSelectedCells = useGardenPlannerStore((state) => state.bulkSelectedCells);
  const toggleBulkSelectedCell = useGardenPlannerStore((state) => state.toggleBulkSelectedCell);

  const updateGarden = useUpdateGarden();
  const toggleCellAvailability = useToggleCellAvailability();

  const cellSlotsMap = useMemo(
    () => createCellSlotsMap(garden.cellSettings),
    [garden.cellSettings]
  );
  const getSlotsForCell = useMemo(() => createGetSlotsForCell(cellSlotsMap), [cellSlotsMap]);

  const bulkSelectedKeys = useMemo(
    () => new Set(bulkSelectedCells.map((cell) => `${cell.row}-${cell.col}`)),
    [bulkSelectedCells]
  );

  const clickContextRef = useRef({
    garden,
    selectedPlant,
    isAvailabilityMode,
    isClearMode,
    isCellSettingsMode,
    getSlotsForCell,
    setSelectedCell,
    updateGarden,
    toggleCellAvailability,
    isBulkEditMode,
    toggleBulkSelectedCell,
  });
  clickContextRef.current = {
    garden,
    selectedPlant,
    isAvailabilityMode,
    isClearMode,
    isCellSettingsMode,
    getSlotsForCell,
    setSelectedCell,
    updateGarden,
    toggleCellAvailability,
    isBulkEditMode,
    toggleBulkSelectedCell,
  };

  const handleCellClick = useCallback((row: number, col: number, event?: unknown) => {
    const ctx = clickContextRef.current;
    return createHandleCellClick(
      ctx.garden,
      ctx.garden.availableCells || [],
      ctx.selectedPlant,
      ctx.isAvailabilityMode,
      ctx.isClearMode,
      ctx.isCellSettingsMode,
      ctx.getSlotsForCell,
      ctx.setSelectedCell,
      ctx.updateGarden,
      ctx.toggleCellAvailability,
      ctx.isBulkEditMode,
      ctx.toggleBulkSelectedCell
    )(row, col, event);
  }, []);

  return (
    <GardenGridView
      rows={garden.rows}
      columns={garden.columns}
      availableCells={garden.availableCells || []}
      plantedPlants={garden.plants}
      selectedPlant={selectedPlant}
      isAvailabilityMode={isAvailabilityMode}
      isCellSettingsMode={isCellSettingsMode}
      isClearMode={isClearMode}
      getSlotsForCell={getSlotsForCell}
      handleCellClick={handleCellClick}
      plants={plants}
      isCellAvailable={isCellAvailable}
      isBulkEditMode={isBulkEditMode}
      bulkSelectedKeys={bulkSelectedKeys}
    />
  );
}
